import { useState, useEffect } from 'react';
import { getSessionActivityAnalytics } from '../../services/sessionService';
import BarChart from '../charts/BarChart';
import LineChart from '../charts/LineChart';

const SessionActivityTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [sessionData, setSessionData] = useState<any>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await getSessionActivityAnalytics(30);
        setSessionData(data);
      } catch (error) {
        console.error('Error loading session data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading session analytics...</p>
      </div>
    );
  }

  if (!sessionData) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>No session data available</p>
      </div>
    );
  }

  const { metrics } = sessionData;

  // Sessions over time chart
  const sessionsOverTimeData = {
    labels: sessionData.sessionsOverTime.map((d: any) => d.date),
    datasets: [
      {
        label: 'Sessions',
        data: sessionData.sessionsOverTime.map((d: any) => d.count),
        borderColor: '#2563eb',
        backgroundColor: 'rgba(37, 99, 235, 0.1)',
        fill: true,
      },
    ],
  };

  // Sessions by hour chart
  const sessionsByHourData = {
    labels: metrics.peakUsageTimes.byHour.map((h: any) => `${h.hour}:00`),
    datasets: [
      {
        label: 'Sessions',
        data: metrics.peakUsageTimes.byHour.map((h: any) => h.count),
        backgroundColor: '#10b981',
      },
    ],
  };

  // Sessions by day of week
  const sessionsByDayData = {
    labels: metrics.peakUsageTimes.byDayOfWeek.map((d: any) => d.dayName),
    datasets: [
      {
        label: 'Sessions',
        data: metrics.peakUsageTimes.byDayOfWeek.map((d: any) => d.count),
        backgroundColor: '#f59e0b',
      },
    ],
  };

  // Session length distribution
  const sessionLengthData = {
    labels: metrics.sessionLengthDistribution.map((d: any) => d.range),
    datasets: [
      {
        label: 'Sessions',
        data: metrics.sessionLengthDistribution.map((d: any) => d.count),
        backgroundColor: '#8b5cf6',
      },
    ],
  };

  return (
    <div style={{ padding: '2rem' }}>
      <h1 style={{ marginBottom: '2rem', fontSize: '1.75rem', fontWeight: 'bold' }}>
        Session & Activity Analytics
      </h1>

      {/* KPI Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Avg Session Duration</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {metrics.averageDuration.toFixed(1)} min
          </h2>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Total Sessions</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {metrics.totalSessions.toLocaleString()}
          </h2>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Bounce Rate</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {metrics.bounceRate.toFixed(1)}%
          </h2>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Deep Engagement</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {metrics.deepEngagementSessions}
          </h2>
          <p style={{ margin: 0, color: '#999', fontSize: '0.75rem' }}>
            Sessions &gt; 30 min
          </p>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Avg Sessions/User/Day</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {metrics.sessionsPerUser.daily.toFixed(2)}
          </h2>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Avg Time Between</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {metrics.avgTimeBetweenSessions.toFixed(1)} min
          </h2>
        </div>
      </div>

      {/* Charts */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Sessions Over Time</h3>
          <LineChart data={sessionsOverTimeData} height={300} />
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Sessions by Hour of Day</h3>
          <BarChart data={sessionsByHourData} height={300} />
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Sessions by Day of Week</h3>
          <BarChart data={sessionsByDayData} height={300} />
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Session Length Distribution</h3>
          <BarChart data={sessionLengthData} height={300} />
        </div>
      </div>

      {/* Top Sessions Table */}
      {sessionData.topSessions.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}>
          <h3 style={{ padding: '1rem', margin: 0, borderBottom: '1px solid #e0e0e0' }}>
            Top Sessions by Duration
          </h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
                <th style={{ padding: '1rem', textAlign: 'left' }}>User ID</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Duration (min)</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Start Time</th>
              </tr>
            </thead>
            <tbody>
              {sessionData.topSessions.map((session: any, index: number) => (
                <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '1rem', fontSize: '0.875rem', wordBreak: 'break-all' }}>
                    {session.userId.substring(0, 20)}...
                  </td>
                  <td style={{ padding: '1rem' }}>{session.duration.toFixed(1)}</td>
                  <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                    {session.timestamp.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SessionActivityTab;

